import {hpcAttrs} from '@github-ui/hpc'
import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {testIdProps} from '@github-ui/test-id-props'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {Banner, Heading, Spinner} from '@primer/react'
import {AriaAlert} from '@primer/react/experimental'
import {useCallback, useId, useMemo, useRef, useState} from 'react'

import {LABEL_ARCHIVE_TYPE, type LabelArchiveType} from './constants/values'
import {EmptyState} from './EmptyState'
import {LabelListPagination} from './LabelListPagination'
import {LabelRow} from './LabelRow'
import {LabelSortMenu} from './LabelSortMenu'
import type {LabelListNode, LabelNode, RepositoryNode} from './repository-label-index-page-query-types'
import styles from './RepositoryLabel.module.css'
import {isArchivedParam, serializeArchivedParam} from './utils/archived-param'
import {LABELS_CONNECTION_PATH} from './utils/label-connection-operations'

export type LabelRepoFragment = Pick<RepositoryNode, 'id' | 'nameWithOwner' | 'isWritable' | 'viewerCanPush' | 'labels'>
export type LabelListFragment = Pick<LabelListNode, 'edges' | 'totalCount'>

type LabelListProps = {
  repository: LabelRepoFragment
  onCreateLabel?: () => void
  queryKey?: RouteQueryKey
  archiveType?: LabelArchiveType
  isLoading?: boolean
  isArchivingEnabled?: boolean
}

type LabelListInternalProps = {
  labels: LabelListFragment
  onCreateLabel?: () => void
  queryKey?: RouteQueryKey
  viewerCanPush: boolean
  isActionsAvailable: boolean
  repositoryId: string
  repositoryNameWithOwner: string
  archiveType?: LabelArchiveType
  isLoading?: boolean
  isArchivingEnabled?: boolean
}

type ListItemHeaderProps = {
  nodes: LabelNode[]
  dataCount: number
  repositoryNameWithOwner: string
  archiveType?: LabelArchiveType
  metadataTitle: React.ReactNode
}

function ListItemsHeader({nodes, dataCount, repositoryNameWithOwner, archiveType, metadataTitle}: ListItemHeaderProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const {ARCHIVED, UNARCHIVED} = LABEL_ARCHIVE_TYPE

  const actions = useMemo(() => {
    if (nodes.length === 0 || searchParams.get('q')) return []
    else {
      return [
        {
          key: 'label-sort-menu',
          render: () => <LabelSortMenu />,
        },
      ]
    }
  }, [nodes.length, searchParams])

  const sectionFilters = useMemo(() => {
    if (!archiveType) return undefined

    const isArchived = isArchivedParam(searchParams.get('archived'))
    const basePath = `/${repositoryNameWithOwner}/labels`

    const getHref = (filterArchiveType: LabelArchiveType) => {
      const params = new URLSearchParams(searchParams)
      params.delete('page')

      /**
       * Always set the `archived` query parameter explicitly to:
       * 1. Keep cache keys consistent (/labels and /labels?archived=false would duplicate cache entries)
       * 2. Make URLs shareable/bookmarkable with explicit state
       * 3. Keep the current state clear for debugging
       *
       * Note: The system will default to UNARCHIVED (active labels) when the parameter is missing,
       * but explicit parameters prevent cache duplication and make intent clearer.
       */
      params.set('archived', serializeArchivedParam(filterArchiveType))
      const paramString = params.toString()
      return paramString ? `${basePath}?${paramString}` : basePath
    }

    const onClick = (event: React.MouseEvent<HTMLAnchorElement>, filterArchiveType: LabelArchiveType) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return

      event.preventDefault()
      navigate(getHref(filterArchiveType), {preventAutofocus: true})
    }

    // Only show count for the currently selected tab
    const activeCount = isArchived ? undefined : dataCount
    const archivedCount = isArchived ? dataCount : undefined

    return [
      <ListViewSectionFilterLink
        key="active"
        title="Active"
        isSelected={!isArchived}
        count={activeCount !== undefined ? activeCount.toLocaleString() : undefined}
        href={getHref(UNARCHIVED)}
        onClick={event => onClick(event, UNARCHIVED)}
      />,
      <ListViewSectionFilterLink
        key="archived"
        title="Archived"
        isSelected={isArchived}
        count={archivedCount !== undefined ? archivedCount.toLocaleString() : undefined}
        href={getHref(ARCHIVED)}
        onClick={event => onClick(event, ARCHIVED)}
      />,
    ]
  }, [ARCHIVED, UNARCHIVED, archiveType, dataCount, navigate, repositoryNameWithOwner, searchParams])

  return (
    <ListViewMetadata
      title={metadataTitle}
      actions={actions}
      actionsLabel="Label actions"
      sectionFilters={sectionFilters}
    />
  )
}

export function LabelList({
  repository,
  onCreateLabel,
  queryKey,
  archiveType,
  isLoading,
  isArchivingEnabled,
}: LabelListProps) {
  // Returns `repository` unchanged when nothing is pending.
  const optimisticConnections = useMemo(
    () => [{ownerId: repository.id, connectionPath: LABELS_CONNECTION_PATH}],
    [repository.id],
  )
  const mergedRepository = useOptimisticData(repository, {connections: optimisticConnections})
  const labels = mergedRepository.labels
  if (!labels) {
    throw new Error('labels data is null or undefined')
  }
  const isActionsAvailable = repository.isWritable && repository.viewerCanPush
  const viewerCanPush = repository.viewerCanPush

  return (
    <LabelListInternal
      labels={labels}
      onCreateLabel={onCreateLabel}
      queryKey={queryKey}
      viewerCanPush={viewerCanPush}
      isActionsAvailable={isActionsAvailable}
      repositoryId={repository.id}
      repositoryNameWithOwner={repository.nameWithOwner}
      archiveType={archiveType}
      isLoading={isLoading}
      isArchivingEnabled={isArchivingEnabled}
    />
  )
}

function LabelListInternal({
  labels,
  onCreateLabel,
  queryKey,
  viewerCanPush,
  isActionsAvailable,
  repositoryId,
  repositoryNameWithOwner,
  archiveType,
  isLoading,
  isArchivingEnabled = false,
}: LabelListInternalProps) {
  const [archiveError, setArchiveError] = useState('')
  const listRef = useRef<HTMLElement | undefined>(undefined)
  const listHeadingRef = useRef<HTMLHeadingElement>(null)
  const listHeadingId = useId()
  const nodes = useMemo(() => {
    // telay types mark description as possibly undefined, but LabelRow expects `string | null`
    // its temporary until LabelList is also migrated off Relay (https://github.com/github/planning-tracking/issues/3053).
    return (
      labels?.edges
        ?.map(edge => (edge?.node ? {...edge.node, description: edge.node.description ?? null} : null))
        .filter(node => !!node) || []
    )
  }, [labels])

  const dataCount = labels?.totalCount || 0
  const metadataTitle = <span aria-live="polite">{dataCount === 1 ? `1 label` : `${dataCount} labels`}</span>

  const labelIds = useMemo(() => nodes.map(node => node.id), [nodes])

  const focusLabelAction = useCallback((labelId: string) => {
    const row = Array.from(listRef.current?.children ?? []).find(
      element => element.getAttribute('data-label-id') === labelId,
    )
    const trigger = row?.querySelector<HTMLButtonElement>('button[aria-expanded]')
    trigger?.focus({preventScroll: true})
    return trigger !== null && trigger !== undefined
  }, [])

  const focusAfterOptimisticRemove = useCallback(
    (labelId: string) => {
      const index = labelIds.indexOf(labelId)
      const adjacentLabelId = labelIds[index + 1] ?? labelIds[index - 1]

      requestAnimationFrame(() => {
        if (!adjacentLabelId || !focusLabelAction(adjacentLabelId)) {
          listHeadingRef.current?.focus({preventScroll: true})
        }
      })
    },
    [focusLabelAction, labelIds],
  )

  const focusAfterArchiveRollback = useCallback(
    (labelId: string) => {
      requestAnimationFrame(() => {
        if (!focusLabelAction(labelId)) listHeadingRef.current?.focus({preventScroll: true})
      })
    },
    [focusLabelAction],
  )

  return (
    <div className={styles.labelListWrapper} {...hpcAttrs()}>
      {isLoading ? (
        <div role="status" className={styles.loadingContainer}>
          <Spinner size="medium" />
          <span className="sr-only">Loading labels</span>
        </div>
      ) : (
        <>
          {archiveError && (
            <Banner
              className="tmp-mb-3"
              title="Error"
              description={<AriaAlert>{archiveError}</AriaAlert>}
              variant="critical"
              role="alert"
            />
          )}
          <Heading ref={listHeadingRef} id={listHeadingId} as="h2" className="sr-only" tabIndex={-1}>
            Labels
          </Heading>
          <ListView
            {...testIdProps('repository-label-list-view')}
            listRef={listRef}
            ariaLabelledBy={listHeadingId}
            title="Labels"
            metadata={
              <ListItemsHeader
                nodes={nodes}
                dataCount={dataCount}
                repositoryNameWithOwner={repositoryNameWithOwner}
                archiveType={archiveType}
                metadataTitle={metadataTitle}
              />
            }
            titleHeaderTag="h2"
            // role="status" for the "no matches" case since that message is rendered dynamically after filtering.
            as={nodes.length > 0 ? 'ul' : 'div'}
            role={nodes.length > 0 ? 'list' : 'status'}
          >
            {nodes.length > 0 &&
              nodes.map(node => (
                <LabelRow
                  key={node.id}
                  label={node}
                  isActionsAvailable={isActionsAvailable}
                  viewerCanPush={viewerCanPush}
                  labelIds={labelIds}
                  repositoryId={repositoryId}
                  repositoryNameWithOwner={repositoryNameWithOwner}
                  queryKey={queryKey}
                  setArchiveError={setArchiveError}
                  isArchivingEnabled={isArchivingEnabled}
                  onArchiveOptimisticRemove={focusAfterOptimisticRemove}
                  onArchiveRollback={focusAfterArchiveRollback}
                />
              ))}
            {nodes.length === 0 && (
              <EmptyState
                noLabelsCreated={dataCount === 0}
                viewerCanPush={viewerCanPush}
                onCreateLabel={onCreateLabel}
                isArchivedView={archiveType === LABEL_ARCHIVE_TYPE.ARCHIVED}
              />
            )}
          </ListView>
          {nodes.length > 0 && <LabelListPagination dataCount={dataCount} />}
        </>
      )}
    </div>
  )
}
